import React, { useContext, useState, useEffect } from 'react';
import styled from 'styled-components';
import { ReactComponent as EditIcon } from '../../common/icons/edit.svg';
import withFallback from '../../common/image-with-fallback';
import stripHtmlToText from '../../common/utils/strip-html';
import {
  updateOffer,
  getAddedOffers,
  getProductOriginalPrice,
  availableProductVariants,
  isProductUnavailableOrLoading,
} from '../contexts/products';
import ProductsContext from '../contexts/ProductsContext';
import RegtriggeringAnimatedCheckmark from './elements/RegtriggeringAnimatedCheckmark';
import { HTMLSectionWithLink } from './elements/Link';
import Select from './elements/Select';
import Card, { Threshold, CardElement } from './Card';
import handleConfirmation from '../utils/adaHandler';

function productLink(
  text,
  product,
  variantTitle,
  display_product_names_as_links,
) {
  const parts = text.split('PRODUCTLINK');
  if (parts.length === 1) {
    return <HTMLSectionWithLink>{parts[0]}</HTMLSectionWithLink>;
  }
  let title = stripHtmlToText(product.title) + (variantTitle || '');
  if (display_product_names_as_links) {
    title = `<a href="${product.url}">${title}</a>`;
  }
  parts.splice(1, 0, title);

  return <HTMLSectionWithLink>{parts.join('')}</HTMLSectionWithLink>;
}

function useVariants(offer, product) {
  const [variant, setVariant] = useState();

  function changeVariant(event) {
    const variantId = Number(event.target.value);
    const productVariants = availableProductVariants(product, offer);
    const selectedVariant = productVariants.find((v) => v.id === variantId);
    updateOffer(offer, selectedVariant);
    setVariant(selectedVariant);
  }

  // Initial variant selection
  useEffect(() => {
    if (isProductUnavailableOrLoading(product)) return;
    const addedOffer = getAddedOffers().find((o) => o.offerId === offer.id);
    const productVariants = availableProductVariants(product, offer);
    const variant =
      productVariants.find(
        (v) => v.id === (addedOffer && addedOffer.productVariantId),
      ) || productVariants.find((v) => v.available);

    if (variant) {
      updateOffer(offer, variant);
    }
    setVariant(variant);
  }, [product, offer]);

  const productVariants = availableProductVariants(product, offer);
  return { productVariants, changeVariant, variant };
}

const EditButton = styled.span`
  position: absolute;
  bottom: 8px;
  right: 8px;
  padding: 4px;
  border-radius: 3px;
  cursor: pointer;

  &:hover {
    background: rgba(81, 184, 60, 0.15);
  }
`;

export default ({ offer, onEdit, adminProduct = null }) => {
  const productsCtx = useContext(adminProduct ? {} : ProductsContext);
  const getProduct = !adminProduct && productsCtx.getProduct;
  const product = adminProduct || getProduct(offer.shopify_product_handle);
  const { productVariants, variant, changeVariant } = useVariants(
    offer,
    product,
  );

  if (!variant || !product || !productVariants) {
    return <CardElement />;
  }

  return (
    <Card
      offer={offer}
      image={
        <img
          src={withFallback(
            (variant && variant.featured_image && variant.featured_image.src) ||
              variant.image ||
              product.featured_image,
          )}
          alt="Product"
        />
      }
      animatedCheckmarkOverride={
        productVariants.length > 1 && (
          <RegtriggeringAnimatedCheckmark animationTriggerValue={variant} />
        )
      }
      originalPrice={getProductOriginalPrice(variant)}
      content={
        <Threshold offer={offer}>
          {({ passed }) => {
            return passed && productVariants.length > 1 ? (
              <Select onChange={changeVariant} value={variant.id}>
                {productVariants.map((v) => (
                  <option key={v.id} value={v.id} disabled={!v.available}>
                    {v.title}
                  </option>
                ))}
              </Select>
            ) : null;
          }}
        </Threshold>
      }
      baseContent={
        onEdit && (
          <EditButton
            data-cy="multiple-offer-edit"
            onClick={onEdit}
            tabIndex={0}
            onKeyPress={handleConfirmation(onEdit)}
          >
            <EditIcon />
          </EditButton>
        )
      }
    >
      <Threshold offer={offer}>
        {({ passed }) => {
          const variantTitle =
            offer.shopify_product_variants &&
            offer.shopify_product_variants.length === 1
              ? ` - ${variant.title}`
              : null;
          if (passed) {
            return productLink(
              offer.subtitle_over_threshold || 'free gift: PRODUCTLINK',
              product,
              variantTitle,
              offer.display_product_names_as_links,
            );
          }
          return productLink(
            offer.subtitle_under_threshold || 'to get a free gift: PRODUCTLINK',
            product,
            variantTitle,
            offer.display_product_names_as_links,
          );
        }}
      </Threshold>
    </Card>
  );
};
